import { useState, useEffect, useRef, ReactNode } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Input } from '../ui/Input';
import { Pagination } from './Pagination';
import { TableShimmer } from './TableShimmer';
import { useDebounce } from '@hooks/useDebounce';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

export interface ColumnDefinition<T> {
  name: string;
  title: string;
  value: (item: T) => ReactNode;
  sortable?: boolean;
  sortKey?: string;
  className?: string;
}

interface TableCardProps<T> {
  columns: ColumnDefinition<T>[];
  data: T[];
  loading?: boolean;
  error?: string;
  onRowClick?: (item: T) => void;
  emptyMessage?: string;
  className?: string;
  
  // Pagination
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  
  // Sorting
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  
  // Search
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;

  // Additional features
  showPagination?: boolean;

  /** Extra controls (status/category filters, etc.) rendered next to the search bar */
  toolbar?: ReactNode;
  /** Column-agnostic action buttons (edit/delete) appended as a final, unsortable column */
  rowActions?: (item: T) => ReactNode;
}

export function TableCard<T extends { id: string | number }>({
  columns,
  data,
  loading = false,
  error,
  onRowClick,
  emptyMessage = 'No data available',
  className,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 20,
  onPageChange,
  sortBy,
  sortDirection = 'asc',
  onSort,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearch,
  showPagination = true,
  toolbar,
  rowActions,
}: TableCardProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Callers pass a fresh onSearch function on every render, so it must not be a
  // dependency below — otherwise unrelated re-renders (e.g. page changes) would
  // re-fire the search callback and reset pagination back to page 1.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    onSearchRef.current?.(debouncedSearch);
  }, [debouncedSearch]);

  const handleSort = (column: ColumnDefinition<T>) => {
    if (!column.sortable || !onSort) return;

    const columnKey = column.sortKey || column.name;
    const newDirection =
      sortBy === columnKey && sortDirection === 'asc' ? 'desc' : 'asc';
    
    onSort(columnKey, newDirection);
  };

  return (
    <div
      className={twMerge(
        clsx('bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden', className)
      )}
    >
      {/* Search bar + toolbar */}
      {(searchable || toolbar) && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:items-center">
          {searchable && (
            <div className="relative flex-1 min-w-0">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
          {toolbar && <div className="flex items-center gap-2 flex-shrink-0">{toolbar}</div>}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border-b border-danger-200 dark:border-danger-800">
          <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          {/* Table header */}
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.name}
                  className={twMerge(
                    clsx(
                      'px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider',
                      column.sortable && 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-800',
                      column.className
                    )
                  )}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.title}</span>
                    {column.sortable && sortBy === (column.sortKey || column.name) && (
                      <span>
                        {sortDirection === 'asc' ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              {rowActions && <th className="px-6 py-3 w-px" />}
            </tr>
          </thead>

          {/* Table body */}
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (rowActions ? 1 : 0)} className="p-0">
                  <TableShimmer rows={itemsPerPage} columns={columns.length} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (rowActions ? 1 : 0)}
                  className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr
                  key={item.id}
                  className={clsx(
                    onRowClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
                  )}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.name}
                      className={twMerge(
                        clsx(
                          'px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100',
                          column.className
                        )
                      )}
                    >
                      {column.value(item)}
                    </td>
                  ))}
                  {rowActions && (
                    <td
                      className="px-6 py-4 whitespace-nowrap text-right text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {rowActions(item)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && !loading && data.length > 0 && onPageChange && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
